import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Chip,
} from "@material-ui/core";
import { CloudUpload, GetApp } from "@material-ui/icons";
import axios from "axios";

const SimpleBulkUploadDialog = ({
  open,
  onClose,
  onUploadSuccess,
  axiosConfig,
  title = "Bulk Upload",
  uploadUrl,
  templateUrl,
  templateFilename = "bulk-upload-template.csv",
  accept = ".csv,.xlsx,.xls",
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const reset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setUploading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = (file) => {
    if (!file || uploading) return;
    setSelectedFile(file);
    setUploadResult(null);
  };

  const downloadTemplate = async () => {
    const response = await axios.get(`${templateUrl}?format=csv`, {
      ...axiosConfig,
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await axios.post(uploadUrl, formData, axiosConfig);
      setUploadResult(response.data);
      if (onUploadSuccess) onUploadSuccess(response.data);
    } catch (error) {
      setUploadResult({
        error:
          error?.response?.data?.message ||
          error.message ||
          "Upload failed",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Download a template, then upload your CSV/XLSX file.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetApp />}
            onClick={downloadTemplate}
          >
            Download CSV Template
          </Button>
        </Box>

        <Box
          p={3}
          textAlign="center"
          style={{
            border: "2px dashed #ccc",
            borderRadius: 4,
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
          }}
          onClick={() => {
            if (!uploading) document.getElementById("simple-bulk-file-input").click();
          }}
        >
          <CloudUpload style={{ fontSize: 40, color: "#009E60" }} />
          <Typography variant="subtitle1">
            {selectedFile ? selectedFile.name : "Drop file here or click to browse"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Supported: CSV, XLSX
          </Typography>
          <input
            id="simple-bulk-file-input"
            type="file"
            accept={accept}
            style={{ display: "none" }}
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
          />
        </Box>

        {uploading && (
          <Box mt={2}>
            <LinearProgress />
          </Box>
        )}

        {uploadResult && (
          <Box mt={2}>
            {uploadResult.error ? (
              <Typography color="error">{uploadResult.error}</Typography>
            ) : (
              <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                <Chip size="small" label={`In File: ${uploadResult.summary?.totalInFile || 0}`} />
                <Chip size="small" color="primary" label={`Created: ${uploadResult.summary?.created || 0}`} />
                <Chip size="small" label={`Updated: ${uploadResult.summary?.updated || 0}`} />
                <Chip size="small" label={`Failed: ${uploadResult.summary?.failed || 0}`} />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{uploadResult ? "Close" : "Cancel"}</Button>
        {selectedFile && !uploading && !uploadResult && (
          <Button
            variant="contained"
            style={{ backgroundColor: "#009E60", color: "#fff" }}
            onClick={handleUpload}
          >
            Upload
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SimpleBulkUploadDialog;
